import  { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Users, TrendingUp, Shield, Zap, Globe, CreditCard, Play, Menu, X, ChevronDown } from 'lucide-react';
import { SEO } from './shared/SEO';

export function LandingPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Create professional invoices in seconds with our AI-powered interface'
    },
    {
      icon: Globe,
      title: 'Multi-Currency',
      description: 'Support for 150+ currencies with real-time exchange rates'
    },
    {
      icon: CreditCard,
      title: 'Payment Integration',
      description: 'Accept payments via Stripe, PayPal, and bank transfers'
    },
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'Your data is protected with enterprise-grade encryption'
    },
    {
      icon: TrendingUp,
      title: 'AI Analytics',
      description: 'Get intelligent insights to grow your business faster'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Invite team members and manage permissions effortlessly'
    }
  ];



  const plans = [
    {
      name: 'Starter',
      price: '$0',
      period: 'forever',
      description: 'Perfect for getting started',
      features: [
        'Up to 5 invoices per month',
        'Basic invoice templates',
        'Email support',
        'Basic analytics',
        'Payment links'
      ],
      cta: 'Get Started Free',
      popular: false,
      color: 'border-gray-200'
    },
    {
      name: 'Professional',
      price: '$19',
      period: 'per month',
      description: 'For growing businesses',
      features: [
        'Unlimited invoices',
        'Custom branding',
        'Payment integrations',
        'AI-powered insights',
        'Team collaboration',
        'Priority support',
        'Advanced analytics',
        'Automated reminders'
      ],
      cta: 'Start Free Trial',
      popular: true,
      color: 'border-green-500'
    },
    {
      name: 'Enterprise',
      price: '$49',
      period: 'per month',
      description: 'For large organizations',
      features: [
        'Everything in Professional',
        'White-label solution',
        'API access',
        'Custom integrations',
        'Dedicated support',
        'SLA guarantee',
        'Advanced security',
        'Custom workflows'
      ],
      cta: 'Contact Sales',
      popular: false,
      color: 'border-purple-500'
    }
  ];

  const faqs = [
    {
      question: 'How does the AI-powered invoicing work?',
      answer: 'Our AI analyzes your business patterns, client behavior, and payment history to provide intelligent recommendations for pricing, payment terms, and follow-up strategies to maximize your revenue.'
    },
    {
      question: 'Can I customize my invoice templates?',
      answer: 'Yes! You can fully customize your invoice templates with your branding, colors, logo, and choose from multiple professional layouts. We also offer white-label solutions for agencies.'
    },
    {
      question: 'What payment methods do you support?',
      answer: 'We integrate with Stripe, PayPal, bank transfers, and 50+ payment gateways worldwide. Your clients can pay with credit cards, debit cards, ACH, and digital wallets.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use bank-level encryption, SOC 2 compliance, and regular security audits. Your data is stored in secure data centers with 99.9% uptime guarantee.'
    },
    {
      question: 'Can I try it before purchasing?',
      answer: 'Yes! We offer a 14-day free trial with full access to all Professional features. No credit card required to start.'
    }
  ];

  const integrations = [
    { name: 'Stripe', logo: '💳' },
    { name: 'PayPal', logo: '🅿️' },
    { name: 'QuickBooks', logo: '📊' },
    { name: 'Xero', logo: '📈' },
    { name: 'Slack', logo: '💬' },
    { name: 'Zapier', logo: '⚡' }
  ];

  return (
    <><SEO
      title="FreelancePay - Freelancer Payment & Invoice Management Platform"
      description="Streamline your freelance business with FreelancePay. Manage invoices, accept payments, track expenses, and handle client relationships all in one place. Built for freelancers by freelancers."
      keywords={[
        'freelance management',
        'invoice software',
        'payment platform',
        'freelancer tools',
        'client management',
        'business automation',
        'expense tracking',
        'online payments',
        'freelance business',
        'professional invoicing'
      ]}
      type="website" /><div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-3" onClick={handleNavClick}>
                <div className="p-2 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-900">FreelancePay</span>
              </Link>

              <nav className="hidden md:flex items-center space-x-8">
                <a href="#features" className="text-gray-600 hover:text-green-600 transition-colors font-medium">Features</a>
                <a href="#pricing" className="text-gray-600 hover:text-green-600 transition-colors font-medium">Pricing</a>
                <a href="#integrations" className="text-gray-600 hover:text-green-600 transition-colors font-medium">Integrations</a>
                <Link
                  to="/signin"
                  className="text-gray-600 hover:text-green-600 transition-colors font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-medium"
                >
                  Get Started
                </Link>
              </nav>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>

            {/* Mobile menu */}
            {isMenuOpen && (
              <div className="md:hidden py-4 border-t border-gray-100">
                <div className="flex flex-col space-y-4">
                  <a
                    href="#features"
                    className="text-gray-600 hover:text-green-600 transition-colors"
                    onClick={handleNavClick}
                  >
                    Features
                  </a>
                  <a
                    href="#pricing"
                    className="text-gray-600 hover:text-green-600 transition-colors"
                    onClick={handleNavClick}
                  >
                    Pricing
                  </a>
                  <a
                    href="#integrations"
                    className="text-gray-600 hover:text-green-600 transition-colors"
                    onClick={handleNavClick}
                  >
                    Integrations
                  </a>
                  <Link
                    to="/signin"
                    className="text-gray-600 hover:text-green-600 transition-colors"
                    onClick={handleNavClick}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all text-center"
                    onClick={handleNavClick}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-50 via-white to-emerald-50 py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-6">
                <Zap className="h-4 w-4 mr-2" />
                AI-Powered Invoicing Platform
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                Get Paid
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent block">
                  40% Faster
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                The most intelligent invoicing platform for freelancers and agencies. Create stunning invoices,
                automate follow-ups, and get AI-powered insights to grow your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/signup"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all transform hover:scale-105 flex items-center justify-center text-lg font-semibold shadow-lg"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button className="flex items-center justify-center px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:border-green-500 hover:text-green-600 transition-all text-lg font-semibold">
                  <Play className="mr-2 h-5 w-5" />
                  Watch Demo
                </button>
              </div>
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-1" />
                  14-day free trial
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-1" />
                  No credit card required
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-1" />
                  Cancel anytime
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-gray-500 mb-8">Trusted by 50,000+ professionals worldwide</p>
              <div className="flex items-center justify-center space-x-8 opacity-60">
                {['Freelancer', 'Agency', 'Consultant', 'Designer', 'Developer'].map((type) => (
                  <div key={type} className="text-gray-400 font-semibold">{type}</div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Everything you need to get paid faster
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Powerful AI-driven features designed to streamline your invoicing process and accelerate your business growth.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="group p-8 rounded-2xl border border-gray-200 hover:border-green-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50">
                    <div className="p-3 bg-green-100 rounded-xl w-fit mb-6 group-hover:bg-green-200 transition-colors">
                      <Icon className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section id="integrations" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Integrates with your favorite tools
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Connect FreelancePay with the tools you already use to streamline your workflow.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {integrations.map((integration, index) => (
                <div key={index} className="flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="text-4xl mb-3">{integration.logo}</div>
                  <p className="font-medium text-gray-900">{integration.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Simple, transparent pricing
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Choose the plan that fits your business. Upgrade or downgrade at any time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative p-8 rounded-2xl border-2 ${plan.color} ${plan.popular
                      ? 'shadow-2xl scale-105 bg-gradient-to-br from-green-50 to-emerald-50'
                      : 'shadow-lg bg-white hover:shadow-xl'} transition-all duration-300`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    <div className="mb-8">
                      <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-gray-600">/{plan.period}</span>
                    </div>

                    <ul className="space-y-4 mb-8 text-left">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/signup"
                      className={`w-full py-4 px-6 rounded-xl font-semibold transition-all block text-center ${plan.popular
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
                    >
                      {plan.cta}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about FreelancePay
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${openFaq === index ? 'transform rotate-180' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to transform your invoicing?
            </h2>
            <p className="text-xl text-green-100 mb-8 leading-relaxed">
              Join thousands of professionals who trust FreelancePay to manage their invoicing and get paid faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="bg-white text-green-600 px-8 py-4 rounded-xl hover:bg-gray-100 transition-colors text-lg font-semibold shadow-lg"
              >
                Start Your Free Trial
              </Link>
              <button className="border-2 border-white text-white px-8 py-4 rounded-xl hover:bg-white hover:text-green-600 transition-colors text-lg font-semibold">
                Schedule Demo
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg">
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-xl font-bold">FreelancePay</span>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  The most intelligent invoicing platform for freelancers and agencies worldwide.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-6">Product</h3>
                <ul className="space-y-3 text-gray-400">
                  <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-6">Company</h3>
                <ul className="space-y-3 text-gray-400">
                  <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-6">Support</h3>
                <ul className="space-y-3 text-gray-400">
                  <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400">&copy; 2024 FreelancePay. All rights reserved.</p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Cookies</a>
              </div>
            </div>
          </div>
        </footer>
      </div></>
  );
}